import { EntityLink } from 'rfa-react-core';
import { formatDate, momentNZ } from 'app/utilities/date-time';
import Icon from 'app/components/partials/icon';
import PropTypes from 'prop-types';
import truncate from 'app/utilities/truncate';
import React, { useEffect, useState } from 'react';

const TITLE_CHARACTER_LIMIT = 60;
const DESCRIPTION_CHARACTER_LIMIT = 80;

/**
 * Upcoming published events for a content block.
 * Past events are hidden; remaining items are sorted soonest-first.
 * @param {Array} items Event items from the CMS block payload.
 * @returns {Array} Filtered and sorted event items.
 */
function prepareEvents(items = []) {
    const today = momentNZ().startOf('day');

    return items
        .filter((item) => {
            const { start_date: startDate, end_date: endDate } = item.data || {};
            const eventEnd = endDate || startDate;

            if (!eventEnd) {
                return false;
            }

            return momentNZ(eventEnd).isSameOrAfter(today, 'day');
        })
        .sort((a, b) => {
            const aStart = momentNZ(a.data.start_date).valueOf();
            const bStart = momentNZ(b.data.start_date).valueOf();

            return aStart - bStart;
        });
}

function renderTile(item, index) { // eslint-disable-line require-jsdoc
    const {
        name,
        thumbnail,
        short_description: description,
        venues,
        start_date: startDate,
        end_date: endDate
    } = item.data;

    const formattedDate = formatDate(startDate, endDate);

    return (
        <article className="new-tile new-tile-event" key={index}>
            <EntityLink
                item={item}
                promoterBuName="Auckland Stadiums"
                className="new-tile-inner"
            >
                <div className="new-tile-image" style={{ backgroundImage: `url(${thumbnail})` }} />
                <div className="new-tile-description">
                    {formattedDate && <p className="event-date">{formattedDate}</p>}
                    <h5>{truncate(name, TITLE_CHARACTER_LIMIT)}</h5>
                    {Boolean(description) && <p>{truncate(description, DESCRIPTION_CHARACTER_LIMIT)}</p>}
                    {venues && (
                        <p className="location">
                            <Icon name="location" />{venues}
                        </p>
                    )}
                </div>
            </EntityLink>
        </article>
    );
}

function UpcomingEvents(props) { // eslint-disable-line require-jsdoc
    const {
        items,
        title
    } = props;
    const events = prepareEvents(items);
    const resolvedDisplayLimit = Number(props.display_limit);
    const pageSize = resolvedDisplayLimit > 0 ? resolvedDisplayLimit : events.length;
    const [visibleCount, setVisibleCount] = useState(pageSize);

    useEffect(() => {
        setVisibleCount(pageSize);
    }, [pageSize, items]);

    const visibleItems = events.slice(0, visibleCount);
    const hasMore = visibleCount < events.length;

    return (
        <section className="tile-block">
            {title &&
                <header className="tile-block-title || constrain-width">
                    <h2 className="light">{title}</h2>
                </header>
            }
            <div className="constrain-width">
                <div className="tile-block-inner">
                    {visibleItems.map(renderTile)}
                </div>
                {hasMore &&
                    <div className="tile-block-actions">
                        <button
                            type="button"
                            className="button primary"
                            onClick={() => setVisibleCount(events.length)}
                        >
                            Load more
                        </button>
                    </div>
                }
            </div>
        </section>
    );
}

UpcomingEvents.propTypes = {
    items: PropTypes.array.isRequired,
    title: PropTypes.string,
    'display_limit': PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default UpcomingEvents;
